/* ============
 * Orders Index
 * ============
 *
 * This page shows the list of orders.
 */
import { mapGetters, mapMutations } from 'vuex';
import orderService from './../../../../services/order';
import exceptionalHoursService from './../../../../services/businessHourException';
import store from './../../../../store';
import moment from 'moment';
import SearchMixin from './../../../../mixins/search';
import AddAppointment from './../../../../components/addAppointment/addAppointment.vue';
import mediaQuery from './../../../../mixins/mediaQuery';

export default {
  data() {
    return {
      loading: false,
      dateRange: [],
      calendarDay: moment(),
      displayWeekends: false,
    };
  },

  computed: {
    ...mapGetters({
      orders: 'paginatedOrders',
      exceptions: 'businessHourExceptions',
    }),

    ordersList() {
      return this.orders.items.map(o => (
        Object.assign({
          start: o.orderType === 'Appointment' ? `${moment(o.date).format('DD/MM/YYYY')} ${o.time}` : false,
        }, o)
      ));
    },

    timelineDirection() {
      return this.$route.query.appointment === 'done' ? 'past' : 'future';
    },

    isTimeline() {
      return this.$route.query.appointment !== 'calendar';
    },

    weekdays() {
      const result = [];
      const limit = this.displayWeekends ? 7 : 5;
      for (let i = 0; i < limit; i += 1) {
        result.push(moment(this.calendarDay).add(i, 'days').format('dddd, DD'));
      }
      return result;
    },
  },

  watch: {
    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      if (this.$route.query.appointment !== 'calendar') {
        this.setSidebar();
      } else {
        this.unsetInnerSidebar();
      }
      this.dateRange = [];
      this.all();
    },

    calendarDay() {
      this.all();
    },
  },

  mounted() {
    this.calendarDay = !this.isDesktop() ? moment() : moment().startOf('isoweek');

    this.all();

    if (this.$route.query.appointment !== 'calendar') {
      this.setSidebar();
    }
  },

  destroyed() {
    store.dispatch('resetOrders');
    this.unsetInnerSidebar();
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
    }),

    onMqChange() {
      this.calendarDay = !this.isDesktop() ? moment() : moment().startOf('isoweek');
    },

    toggleWeekends() {
      this.displayWeekends = !this.displayWeekends;
    },

    setSidebar() {
      const sidebar = [
        {
          title: this.$t('layouts.sidebar.orders'),
          options: [
            {
              title: this.$t('layouts.sidebar.products'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'Product',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.services'),
              route: {
                name: 'orders.index',
                query: {
                  filter: 'Service',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.all'),
              route: {
                name: 'orders.index',
              },
            },
          ],
          button: {
            title: `+ ${this.$t('static.orders.create.title')}`,
            route: {
              name: 'orders.create',
            },
          },
        },
        {
          title: this.$t('layouts.sidebar.booking'),
          options: [
            {
              title: this.$t('layouts.sidebar.upcoming'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'upcoming',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.done'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'done',
                },
              },
            },
            {
              title: this.$t('layouts.sidebar.calendar'),
              route: {
                name: 'appointments.index',
                query: {
                  appointment: 'calendar',
                },
              },
            },
          ],
          button: {
            title: `+ ${this.$t('static.appointments.create.title')}`,
            route: {
              name: 'orders.create',
            },
          },
        },
      ];

      this.setInnerSidebar(sidebar);
    },

    changeCalendarDay(add, days = 1) {
      if (add) {
        this.calendarDay = moment(this.calendarDay.add(days, 'days'));
      } else {
        this.calendarDay = moment(this.calendarDay.subtract(days, 'days'));
      }
    },

    onRangeChange(name, value) {
      this[name] = value;
      this.$router.push({
        name: 'appointments.index',
      });
    },

    onDateAdd(date) {
      this.setModalContent(() => AddAppointment);
      this.setModalProps({
        create: this.saveAppointment,
        date,
      });
      this.toggleModal(true);
    },

    onDateEdit(date, order) {
      this.setModalContent(() => AddAppointment);
      this.setModalProps({
        create: this.updateAppointment,
        date,
        appointment: order,
        destroy: this.deleteAppointment,
      });
      this.toggleModal(true);
    },

    saveAppointment(data) {
      return orderService.store(data).then(() => {
        this.all();
      });
    },

    updateAppointment(data, id) {
      return orderService.update(id, data).then(() => {
        this.all();
      });
    },

    deleteAppointment(id) {
      return orderService.destroy(id).then(() => {
        this.all();
      });
    },

    getOrderStatus(order) {
      if (order.orderStatus === 'cancelled') {
        return this.$t('static.appointments.index.cancelled');
      }

      const now = moment();
      const start = moment(`${order.date} ${order.time}`);
      const end = moment(`${order.date} ${order.time}`).add(order.duration, 'minutes');
      if (now.isAfter(end)) {
        return this.$t('static.appointments.index.completed');
      }

      if (now.isBefore(start)) {
        return this.$t('static.appointments.index.created');
      }

      return this.$t('static.appointments.index.ongoing');
    },

    /**
     * Fetches paginated orders.
     *
     * @param  {int} page The page number.
     * @param  {int} limit The number of items displayed on a page.
     */
    all(page, limit) {
      if (!this.loading) {
        let appointment = this.$route.query.appointment;
        if (this.$route.query.appointment !== 'calendar' && this.dateRange.length &&
            moment(this.dateRange[0]).isValid() &&
            moment(this.dateRange[1]).isValid()) {
          appointment = `${moment(this.dateRange[0]).format('DD-MM-YYYY')}|${moment(this.dateRange[1]).format('DD-MM-YYYY')}`;
        } else if (this.$route.query.appointment === 'calendar') {
          appointment = `${moment(this.calendarDay).format('DD-MM-YYYY')}|${moment(this.calendarDay).add(7, 'days').format('DD-MM-YYYY')}`;
        }
        this.loading = true;
        const params = {
          page,
          limit,
          search: this.getSearch(),
          appointment,
          now: moment().format('DD-MM-YYYY HH:mm'),
        };
        Promise.all([
          orderService.items(params),
          exceptionalHoursService.all(),
        ])
        .then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      }
    },

     /**
      * Go to edit view for order.
      *
      * @param  {Object} order The order object.
      */
    editOrder(order) {
      this.$router.push({
        name: 'orders.edit',
        params: { id: order.id },
      });
    },
  },

  components: {
    VDateRange: require('./../../../../components/form/dateRange/dateRange.vue'),
    VTimeline: require('./../../../../components/timeline/timeline.vue'),
    VCalendar: require('./../../../../components/calendar/calendar.vue'),
    VDateBar: require('./../../../../components/dateBar/dateBar.vue'),
  },

  mixins: [
    mediaQuery,
    SearchMixin,
  ],
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/appointments/index/index.js